import { Crown, LogOut, Menu, Receipt, Settings, Tags } from 'lucide-react'
import { useState } from 'react'
import { Link, Navigate, NavLink, Outlet, useLocation } from 'react-router-dom'

import { OrgSwitcher } from '@/components/org-switcher'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { authClient, useSession } from '@/lib/auth-client'
import { cn } from '@/lib/utils'
import { trpc } from '@/trpc/client'

const NAV_ITEMS = [
	{ to: '/app', label: 'Items', icon: Receipt, end: true },
	{ to: '/app/categories', label: 'Categories', icon: Tags, end: false },
	{ to: '/app/settings', label: 'Settings', icon: Settings, end: false },
] as const

function NavList({ onNavigate }: { onNavigate?: () => void }) {
	return (
		<nav className="flex flex-col gap-0.5 px-3">
			{NAV_ITEMS.map((item) => (
				<NavLink
					key={item.to}
					to={item.to}
					end={item.end}
					onClick={onNavigate}
					className={({ isActive }) =>
						cn(
							'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
							'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent',
							isActive && 'bg-sidebar-accent text-sidebar-foreground',
						)
					}
				>
					<item.icon className="size-4" />
					{item.label}
				</NavLink>
			))}
		</nav>
	)
}

function SidebarBrand() {
	return (
		<Link
			to="/app"
			className="flex items-center gap-2 px-5 py-4 text-base font-semibold tracking-tight"
		>
			<div className="flex size-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
				<Receipt className="size-4" />
			</div>
			Warrantly
		</Link>
	)
}

function SidebarTop() {
	return (
		<>
			<SidebarBrand />
			<OrgSwitcher />
		</>
	)
}

function PlanBadge({ plan }: { plan: 'free' | 'pro' }) {
	if (plan === 'pro') {
		return (
			<span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-gradient-to-r from-amber-400 via-orange-500 to-pink-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm shadow-orange-500/30 ring-1 ring-inset ring-white/30">
				<Crown className="size-3 drop-shadow" />
				Pro
			</span>
		)
	}
	return (
		<span className="inline-flex shrink-0 items-center rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm shadow-emerald-500/30 ring-1 ring-inset ring-white/30">
			Free
		</span>
	)
}

function UserPanel({ email }: { email: string }) {
	const orgQuery = trpc.organizations.getCurrent.useQuery()
	async function signOut() {
		await authClient.signOut()
		window.location.replace('/')
	}

	return (
		<div className="border-t border-sidebar-border p-3">
			<div className="px-2 pb-2 text-xs text-sidebar-foreground/60">
				Signed in as
			</div>
			<div className="flex items-center gap-2 px-2 pb-3">
				<span
					className="min-w-0 flex-1 truncate text-sm font-medium"
					title={email}
				>
					{email}
				</span>
				{orgQuery.isPending ? (
					<Skeleton className="h-4 w-10 rounded-full" />
				) : orgQuery.isError ? (
					<span
						className="inline-flex shrink-0 items-center rounded-full bg-destructive/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-destructive"
						title={orgQuery.error.message}
					>
						!
					</span>
				) : (
					<PlanBadge plan={orgQuery.data.plan} />
				)}
			</div>
			<Button
				variant="outline"
				size="sm"
				className="w-full justify-start gap-2"
				onClick={signOut}
			>
				<LogOut className="size-4" />
				Sign out
			</Button>
		</div>
	)
}

function DesktopSidebar({ email }: { email: string }) {
	return (
		<aside className="hidden lg:flex w-60 shrink-0 flex-col border-r border-sidebar-border bg-sidebar">
			<SidebarTop />
			<Separator className="bg-sidebar-border" />
			<div className="flex-1 overflow-y-auto py-4">
				<NavList />
			</div>
			<UserPanel email={email} />
		</aside>
	)
}

function MobileTopbar({ email }: { email: string }) {
	const [open, setOpen] = useState(false)
	return (
		<div className="lg:hidden flex h-14 items-center justify-between border-b bg-card px-4">
			<Sheet open={open} onOpenChange={setOpen}>
				<SheetTrigger asChild>
					<Button variant="ghost" size="icon" aria-label="Open menu">
						<Menu className="size-5" />
					</Button>
				</SheetTrigger>
				<SheetContent side="left" className="w-64 p-0">
					<SidebarTop />
					<Separator className="bg-sidebar-border" />
					<div className="flex-1 overflow-y-auto py-4">
						<NavList onNavigate={() => setOpen(false)} />
					</div>
					<UserPanel email={email} />
				</SheetContent>
			</Sheet>
			<Link to="/app" className="font-semibold tracking-tight">
				Warrantly
			</Link>
			<div className="size-9" />
		</div>
	)
}

export function AppLayout() {
	const session = useSession()
	const location = useLocation()

	if (session.isPending) {
		return (
			<div className="flex min-h-dvh">
				<div className="hidden lg:flex w-60 shrink-0 flex-col border-r bg-sidebar p-4 gap-4">
					<Skeleton className="h-8 w-32" />
					<Skeleton className="h-8 w-full" />
					<Skeleton className="h-8 w-full" />
				</div>
				<div className="flex-1 p-8">
					<Skeleton className="h-8 w-48" />
				</div>
			</div>
		)
	}

	if (!session.data) {
		return (
			<Navigate
				to="/login"
				replace
				state={{ from: location.pathname + location.search }}
			/>
		)
	}

	const email = session.data.user.email

	return (
		<div className="flex min-h-dvh bg-background">
			<DesktopSidebar email={email} />
			<div className="flex min-w-0 flex-1 flex-col">
				<MobileTopbar email={email} />
				<main className="flex-1 overflow-y-auto">
					<div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-8 sm:py-10">
						<Outlet />
					</div>
				</main>
			</div>
		</div>
	)
}
